import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SuperHttpClientProvider } from '@app/core/http';

export interface ModuleAgentChatRange {
  start: string;
  end: string;
}

export type ModuleAgentChartKind =
  | 'soil'
  | 'flow'
  | 'soil_control_profile'
  | string;
export type ModuleAgentChartMetric =
  | 'control'
  | 'drain'
  | 'depth'
  | 'instant'
  | 'daily'
  | string;

export interface ModuleAgentChatChartMeta {
  id: string;
  chartKind: ModuleAgentChartKind;
  metric: ModuleAgentChartMetric;
  range: ModuleAgentChatRange;
  title?: string | null;
  note?: string | null;
  sensors?: string[];
}

export interface ModuleAgentChatChartSeries {
  id?: string;
  name: string;
  data: Array<[number, number | null]>;
  type?: 'line' | 'spline' | 'column';
  color?: string;
  zoneAxis?: 'x' | 'y' | 'z';
  zones?: Array<{ value?: number; color?: string }>;
  showInLegend?: boolean;
  linkedTo?: string | null;
}

export interface ModuleAgentChatChartData {
  source: string;
  sourceSeriesKey?: string | null;
  metric: string;
  visualization?: string | null;
  range: ModuleAgentChatRange;
  series: ModuleAgentChatChartSeries[];
}

export interface ModuleAgentChatTextBlock {
  type: 'text';
  text: string;
}

export interface ModuleAgentChatChartBlock {
  type: 'chart';
  chart: ModuleAgentChatChartMeta;
  chartData: ModuleAgentChatChartData;
}

export type ModuleAgentChatRenderBlock =
  | ModuleAgentChatTextBlock
  | ModuleAgentChatChartBlock;

export interface ModuleAgentChatRequest {
  text: string;
  tz?: string;
  locale?: string;
  wantsCharts?: boolean;
}

export interface ModuleAgentChatTurn {
  role: string;
  text: string;
  createdAt?: string | null;
}

export interface ModuleAgentChatMeta {
  simulated?: boolean;
  provider?: string | null;
  hasCharts?: boolean;
  chartsCount?: number;
}

export interface ModuleAgentChatResponse {
  moduleId?: number;
  conversation?: ModuleAgentChatTurn[];
  replyText?: string | null;
  renderBlocks?: ModuleAgentChatRenderBlock[] | null;
  meta?: ModuleAgentChatMeta | null;
}

@Injectable({
  providedIn: 'root'
})
export class ModuleAgentChatService {
  constructor(private $http: SuperHttpClientProvider) {}

  postModuleAgentChat(
    moduleId: number,
    text: string,
    context?: Pick<ModuleAgentChatRequest, 'tz' | 'locale' | 'wantsCharts'>
  ): Observable<ModuleAgentChatResponse> {
    const payload: ModuleAgentChatRequest = {
      text,
      tz: context?.tz,
      locale: context?.locale,
      wantsCharts: context?.wantsCharts
    };

    return this.$http.default.post<ModuleAgentChatResponse>(
      `/admin/module/${moduleId}/agent-chat`,
      payload
    );
  }
}
